import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import performRequest from "network/perform-request";
import {
  membershipChipColor,
  membershipChipLabel,
  membershipCurrencySymbol,
  membershipStatusOptions,
} from "utils/membership-ui-helpers";
import { formatMembershipMonth } from "components/pages/Gestion/financial/financial-helpers";

const formatAmount = (amount, currencyCode) => {
  const symbol = membershipCurrencySymbol(currencyCode);
  const value = amount == null || amount === "" ? "—" : Number(amount).toLocaleString();
  return symbol ? `${symbol} ${value}` : value;
};

const formatDate = (value) => (value ? dayjs(value).format("DD/MM/YYYY") : "—");

const currentCalendarYear = () => dayjs().year();

const isPayableStatus = (status) => status !== "paid" && status !== "void";

const isLivePending = (installment) => {
  const status = installment.payment_status;
  if (status !== "PENDING" && status !== "pending") {
    return false;
  }
  if (!installment.pending_expires_at) {
    return false;
  }

  return dayjs(installment.pending_expires_at).isAfter(dayjs());
};

const installmentLabel = (installment, intl) =>
  installment.due_on
    ? intl.formatMessage({ id: "membership.panel.dueOn" }) + " " + formatMembershipMonth(installment.due_on, intl)
    : `#${installment.id}`;

/**
 * Shared membership billing UI (dues list + pay).
 * @param {{
 *   active: boolean,
 *   user: object,
 *   membershipFromList?: object|null,
 *   loadingMembership?: boolean,
 *   createBody?: object|null,
 *   emptyMessage?: string,
 *   createLabel?: string,
 *   routes: {
 *     list: string,
 *     detail: (membershipId: number|string) => string,
 *     pay: (membershipId: number|string) => string,
 *     paymentLinks?: (membershipId: number|string) => string,
 *     status: (membershipId: number|string) => string,
 *     void?: (membershipId: number|string) => string,
 *   },
 *   collectProof?: boolean,
 *   hasCollectionProcessor?: boolean,
 *   onUpdated?: (membership?: object) => void,
 *   onActionsChange?: (actions: {
 *     membershipId?: number,
 *     selectedCount: number,
 *     paying: boolean,
 *     payLabel: string|null,
 *     onPay: (() => void)|null,
 *     voiding?: boolean,
 *     voidLabel?: string|null,
 *     onVoid?: (() => void)|null,
 *   }) => void,
 * }} props
 */
const MembershipBillingPanel = ({
  active,
  user,
  membershipFromList = null,
  loadingMembership = false,
  createBody = null,
  emptyMessage,
  createLabel,
  routes,
  collectProof = false,
  hasCollectionProcessor = false,
  onUpdated,
  onActionsChange,
}) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [paying, setPaying] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [voidConfirmOpen, setVoidConfirmOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [proofFile, setProofFile] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [statusValue, setStatusValue] = useState("active");
  const [selectedYear, setSelectedYear] = useState(currentCalendarYear());

  const membershipId = detail?.membership?.id || membershipFromList?.id;

  const installmentStatusLabel = (status) => {
    switch (status) {
      case "paid":
        return intl.formatMessage({ id: "membership.installment.paid" });
      case "pending":
        return intl.formatMessage({ id: "membership.installment.pending" });
      case "void":
        return intl.formatMessage({ id: "membership.installment.void" });
      default:
        return status || "—";
    }
  };

  const overdueLabel = (installment) => {
    if (installment.status === "paid") {
      return installment.paid_on
        ? intl.formatMessage({ id: "membership.installment.paidOn" }, { date: formatDate(installment.paid_on) })
        : intl.formatMessage({ id: "membership.installment.paid" });
    }
    if (installment.days_past_due > 0) {
      return intl.formatMessage({ id: "membership.installment.daysLate" }, { days: installment.days_past_due });
    }
    if (installment.due_on && dayjs(installment.due_on).isAfter(dayjs(), "day")) {
      return intl.formatMessage({ id: "membership.installment.upcoming" });
    }
    return intl.formatMessage({ id: "membership.installment.current" });
  };

  const loadDetail = useCallback(
    (year = selectedYear, id = membershipId) => {
      if (!routes?.detail || !id) {
        setDetail(null);
        return Promise.resolve(null);
      }

      setLoading(true);
      setError(null);

      return performRequest("get", routes.detail(id), { year }, true, user)
        .then((response) => {
          const payload = response.data || {};
          setDetail(payload);
          setStatusValue(payload.membership?.status || "active");
          setSelectedYear(payload.calendar_year || year);
          setSelectedIds([]);
          setProofFile(null);
          return payload;
        })
        .catch((err) => {
          setError(err?.response?.data?.message || intl.formatMessage({ id: "membership.panel.loadError" }));
          setDetail(null);
          return null;
        })
        .finally(() => setLoading(false));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [membershipId, routes, selectedYear, user],
  );

  useEffect(() => {
    if (!active) {
      setDetail(null);
      setSelectedIds([]);
      setError(null);
      setSelectedYear(currentCalendarYear());
      return;
    }

    if (membershipId) {
      loadDetail(selectedYear, membershipId);
    } else {
      setDetail(null);
      setError(null);
      setSelectedYear(currentCalendarYear());
    }
  }, [active, membershipFromList?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleYearChange = (year) => {
    setSelectedYear(year);
    setSelectedIds([]);
    loadDetail(year);
  };

  const unpaidInstallments = useMemo(
    () =>
      (detail?.installments || []).filter(
        (item) => isPayableStatus(item.status) && !isLivePending(item),
      ),
    [detail?.installments],
  );

  const awaitingGroups = useMemo(() => {
    const groups = new Map();
    (detail?.installments || []).filter(isLivePending).forEach((installment) => {
      const paymentId = installment.payment_id;
      if (!paymentId) return;
      if (!groups.has(paymentId)) {
        groups.set(paymentId, {
          paymentId,
          paymentUrl: installment.payment_url,
          expiresAt: installment.pending_expires_at,
          installments: [],
        });
      }
      groups.get(paymentId).installments.push(installment);
    });

    return Array.from(groups.values());
  }, [detail?.installments]);

  const selectedPayTotal = useMemo(() => {
    if (selectedIds.length === 0) return 0;
    return (detail?.installments || [])
      .filter((item) => selectedIds.includes(item.id))
      .reduce((sum, item) => sum + Number(item.amount || 0), 0);
  }, [detail?.installments, selectedIds]);

  const yearOptions = useMemo(() => {
    const fromApi = detail?.available_years || [];
    if (fromApi.length > 0) {
      return [...fromApi].sort((a, b) => b - a);
    }
    return [currentCalendarYear()];
  }, [detail?.available_years]);

  const currencyCode = detail?.currency_code;
  const allUnpaidSelected =
    unpaidInstallments.length > 0 && unpaidInstallments.every((item) => selectedIds.includes(item.id));

  const toggleInstallment = (installmentId) => {
    setSelectedIds((current) =>
      current.includes(installmentId) ? current.filter((id) => id !== installmentId) : [...current, installmentId],
    );
  };

  const toggleAllUnpaid = () => {
    if (allUnpaidSelected) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(unpaidInstallments.map((item) => item.id));
  };

  const handleCreateMembership = () => {
    if (!routes?.list || !createBody) return;

    setCreating(true);
    setError(null);

    performRequest("post", routes.list, createBody, true, user)
      .then((response) => {
        const created = response.data || {};
        onUpdated?.(created);
        return performRequest("get", routes.detail(created.id), { year: currentCalendarYear() }, true, user);
      })
      .then((response) => {
        setDetail(response.data || {});
        setStatusValue(response.data?.membership?.status || "active");
        setSelectedYear(response.data?.calendar_year || currentCalendarYear());
        onUpdated?.(response.data?.membership);
      })
      .catch((err) => {
        setError(err?.response?.data?.message || intl.formatMessage({ id: "membership.panel.createError" }));
      })
      .finally(() => setCreating(false));
  };

  const handleStatusChange = (nextStatus) => {
    if (!membershipId || !routes?.status) return;

    setStatusValue(nextStatus);
    performRequest("patch", routes.status(membershipId), { status: nextStatus }, true, user)
      .then(() => loadDetail(selectedYear))
      .then(() => onUpdated?.())
      .catch((err) => {
        setError(err?.response?.data?.message || intl.formatMessage({ id: "membership.panel.statusError" }));
        loadDetail(selectedYear);
      });
  };

  const handlePaySelected = useCallback(() => {
    if (!membershipId || !routes?.pay || selectedIds.length === 0) return;
    if (collectProof && !proofFile) return;

    setPaying(true);
    setError(null);

    const body = collectProof
      ? (() => {
          const formData = new FormData();
          selectedIds.forEach((id) => formData.append("installment_ids[]", id));
          formData.append("method", paymentMethod);
          formData.append("proof", proofFile);
          return formData;
        })()
      : { installment_ids: selectedIds, method: "cash" };

    performRequest("post", `${routes.pay(membershipId)}?year=${selectedYear}`, body, true, user)
      .then((response) => {
        setDetail(response.data || {});
        setSelectedIds([]);
        setProofFile(null);
        onUpdated?.(response.data?.membership);
      })
      .catch((err) => {
        setError(err?.response?.data?.message || intl.formatMessage({ id: "membership.panel.payError" }));
      })
      .finally(() => setPaying(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectProof, membershipId, onUpdated, paymentMethod, proofFile, routes, selectedIds, selectedYear, user]);

  const handleGenerateLink = useCallback(() => {
    if (!membershipId || !routes?.paymentLinks || selectedIds.length === 0 || !hasCollectionProcessor) return;

    setGeneratingLink(true);
    setError(null);

    performRequest(
      "post",
      `${routes.paymentLinks(membershipId)}?year=${selectedYear}`,
      { installment_ids: selectedIds },
      true,
      user,
    )
      .then((response) => {
        const paymentUrl = response.data?.payment_url;
        if (paymentUrl && navigator.clipboard?.writeText) {
          navigator.clipboard.writeText(paymentUrl).catch(() => {});
        }
        enqueueSnackbar(intl.formatMessage({ id: "gestion.financials.linkCopied" }), { variant: "success" });
        setSelectedIds([]);
        return loadDetail(selectedYear, membershipId);
      })
      .then(() => onUpdated?.())
      .catch((err) => {
        setError(err?.response?.data?.message || intl.formatMessage({ id: "membership.panel.payError" }));
      })
      .finally(() => setGeneratingLink(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasCollectionProcessor,
    membershipId,
    onUpdated,
    routes,
    selectedIds,
    selectedYear,
    user,
  ]);

  const handleCopyLink = (url) => {
    if (!url || !navigator.clipboard?.writeText) return;
    navigator.clipboard.writeText(url).then(() => {
      enqueueSnackbar(intl.formatMessage({ id: "gestion.financials.linkCopied" }), { variant: "success" });
    });
  };

  const handleVoidSelected = useCallback(() => {
    if (!membershipId || !routes?.void || selectedIds.length === 0) return;

    setVoiding(true);
    setError(null);

    performRequest(
      "post",
      `${routes.void(membershipId)}?year=${selectedYear}`,
      { installment_ids: selectedIds },
      true,
      user,
    )
      .then((response) => {
        setDetail(response.data || {});
        setSelectedIds([]);
        setVoidConfirmOpen(false);
        onUpdated?.(response.data?.membership);
      })
      .catch((err) => {
        setError(err?.response?.data?.message || intl.formatMessage({ id: "membership.panel.voidError" }));
      })
      .finally(() => setVoiding(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membershipId, onUpdated, routes, selectedIds, selectedYear, user]);

  // Keep the latest pay/void handlers in refs so the actions effect below only
  // depends on primitives. Otherwise unstable parent props (routes/onUpdated)
  // would re-trigger onActionsChange -> parent setState -> render loop.
  const paySelectedRef = useRef(handlePaySelected);
  useEffect(() => {
    paySelectedRef.current = handlePaySelected;
  }, [handlePaySelected]);

  const voidSelectedRef = useRef(handleVoidSelected);
  useEffect(() => {
    voidSelectedRef.current = handleVoidSelected;
  }, [handleVoidSelected]);

  const selectedCount = selectedIds.length;
  const canVoid = Boolean(routes?.void);

  useEffect(() => {
    if (!onActionsChange) return;

    const amountLabel = formatAmount(selectedPayTotal, currencyCode);
    const payLabel =
      selectedCount === 0
        ? null
        : selectedCount === 1
          ? intl.formatMessage({ id: "membership.panel.pay" }, { amount: amountLabel })
          : intl.formatMessage({ id: "membership.panel.payCount" }, { amount: amountLabel, count: selectedCount });

    const voidLabel =
      canVoid && selectedCount > 0
        ? intl.formatMessage({ id: "membership.panel.voidSelected" }, { count: selectedCount })
        : null;

    const inPanelPay = collectProof || Boolean(routes?.paymentLinks);

    onActionsChange({
      membershipId,
      selectedCount,
      paying: paying || generatingLink,
      payLabel,
      onPay: inPanelPay || selectedCount === 0 ? null : () => paySelectedRef.current(),
      voiding,
      voidLabel,
      onVoid: canVoid && selectedCount > 0 ? () => setVoidConfirmOpen(true) : null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canVoid, collectProof, currencyCode, membershipId, onActionsChange, paying, generatingLink, routes?.paymentLinks, selectedCount, selectedPayTotal, voiding]);

  const eligibility = detail?.membership?.eligibility || membershipFromList?.eligibility;
  const viewingPastYear = selectedYear < currentCalendarYear();
  const amountsNotConfigured = detail && detail.amounts_configured === false;

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!membershipId && (loadingMembership || loading) && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!membershipId && !loading && !loadingMembership && (
        <Stack spacing={2} alignItems="flex-start">
          <Typography color="text.secondary">
            {emptyMessage || intl.formatMessage({ id: "membership.panel.empty" })}
          </Typography>
          {createBody && (
            <Button variant="contained" onClick={handleCreateMembership} disabled={creating}>
              {creating
                ? intl.formatMessage({ id: "membership.panel.creating" })
                : createLabel || intl.formatMessage({ id: "membership.panel.create" })}
            </Button>
          )}
        </Stack>
      )}

      {membershipId && loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {membershipId && !loading && detail && (
        <Stack spacing={2}>
          {amountsNotConfigured && (
            <Alert severity="warning">{intl.formatMessage({ id: "membership.panel.amountsNotConfigured" })}</Alert>
          )}

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
          >
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
              <TextField
                select
                size="small"
                label={intl.formatMessage({ id: "membership.panel.status" })}
                value={statusValue}
                onChange={(e) => handleStatusChange(e.target.value)}
                sx={{ minWidth: 180 }}
              >
                {membershipStatusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <Chip size="small" label={membershipChipLabel(eligibility)} color={membershipChipColor(eligibility)} />
            </Stack>

            <TextField
              select
              size="small"
              label={intl.formatMessage({ id: "membership.panel.year" })}
              value={selectedYear}
              onChange={(e) => handleYearChange(Number(e.target.value))}
              sx={{ minWidth: 120 }}
            >
              {yearOptions.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <Stack spacing={0.5}>
            <Stack direction="row" spacing={2} alignItems="baseline">
              <Typography variant="subtitle1">
                {intl.formatMessage({ id: "membership.panel.totalDebt" })}
              </Typography>
              <Typography variant="h6">{formatAmount(detail.total_debt, currencyCode)}</Typography>
              <Typography variant="body2" color="text.secondary">
                {intl.formatMessage({ id: "membership.panel.totalDebtHint" })}
              </Typography>
            </Stack>

            {detail.year_debt != null && detail.year_debt !== detail.total_debt && (
              <Typography variant="body2" color="text.secondary">
                {intl.formatMessage(
                  { id: "membership.panel.yearDebt" },
                  { year: selectedYear, amount: formatAmount(detail.year_debt, currencyCode) },
                )}
              </Typography>
            )}

            {selectedCount > 0 && (
              <Typography variant="body1" sx={{ mt: 0.5 }}>
                {intl.formatMessage(
                  { id: "membership.panel.payTotal" },
                  { amount: formatAmount(selectedPayTotal, currencyCode), count: selectedCount },
                )}
              </Typography>
            )}
          </Stack>

          {viewingPastYear && (detail.installments || []).length === 0 && (
            <Alert severity="info">
              {intl.formatMessage({ id: "membership.panel.noPastInstallments" }, { year: selectedYear })}
            </Alert>
          )}

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allUnpaidSelected}
                    indeterminate={selectedCount > 0 && !allUnpaidSelected}
                    onChange={toggleAllUnpaid}
                    disabled={unpaidInstallments.length === 0}
                  />
                </TableCell>
                <TableCell>{intl.formatMessage({ id: "membership.panel.dueOn" })}</TableCell>
                <TableCell>{intl.formatMessage({ id: "membership.panel.amount" })}</TableCell>
                <TableCell>{intl.formatMessage({ id: "membership.panel.status" })}</TableCell>
                <TableCell>{intl.formatMessage({ id: "membership.panel.aging" })}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(detail.installments || []).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    {intl.formatMessage({ id: "membership.panel.noInstallments" }, { year: selectedYear })}
                  </TableCell>
                </TableRow>
              ) : (
                (detail.installments || []).map((installment) => {
                  const isUnpaid = isPayableStatus(installment.status) && !isLivePending(installment);
                  return (
                    <TableRow key={installment.id} hover selected={selectedIds.includes(installment.id)}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedIds.includes(installment.id)}
                          onChange={() => toggleInstallment(installment.id)}
                          disabled={!isUnpaid}
                        />
                      </TableCell>
                      <TableCell>{formatMembershipMonth(installment.due_on, intl)}</TableCell>
                      <TableCell>{formatAmount(installment.amount, installment.currency_code || currencyCode)}</TableCell>
                      <TableCell>{installmentStatusLabel(installment.status)}</TableCell>
                      <TableCell>{overdueLabel(installment)}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>

          {(collectProof || routes?.paymentLinks) && (
            <Stack spacing={2}>
              <Typography variant="subtitle1">
                {intl.formatMessage({ id: "gestion.financials.registerPayment" })}
              </Typography>
              {selectedIds.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  {intl.formatMessage({ id: "gestion.financials.selectInvoiceToPay" })}
                </Typography>
              )}
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
                <TextField
                  select
                  size="small"
                  label={intl.formatMessage({ id: "gestion.financials.paymentMethod" })}
                  value={paymentMethod}
                  onChange={(event) => setPaymentMethod(event.target.value)}
                  sx={{ minWidth: 180 }}
                  disabled={selectedIds.length === 0}
                >
                  <MenuItem value="cash">{intl.formatMessage({ id: "gestion.financials.methodCash" })}</MenuItem>
                  <MenuItem value="transfer">{intl.formatMessage({ id: "gestion.financials.methodTransfer" })}</MenuItem>
                  <MenuItem value="pos">{intl.formatMessage({ id: "gestion.financials.methodPos" })}</MenuItem>
                </TextField>
                {collectProof && (
                  <Button variant="outlined" component="label" disabled={selectedIds.length === 0}>
                    {proofFile?.name || intl.formatMessage({ id: "gestion.financials.proofFile" })}
                    <input
                      type="file"
                      hidden
                      accept=".pdf,.jpg,.jpeg,.png"
                      disabled={selectedIds.length === 0}
                      onChange={(event) => setProofFile(event.target.files?.[0] || null)}
                    />
                  </Button>
                )}
              </Stack>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
                <Button
                  variant="contained"
                  disabled={paying || selectedIds.length === 0 || (collectProof && !proofFile)}
                  onClick={() => paySelectedRef.current()}
                >
                  {intl.formatMessage({ id: "gestion.financials.confirmPayment" })}
                </Button>
                {routes?.paymentLinks && (
                  <Button
                    variant="outlined"
                    disabled={
                      generatingLink || paying || selectedIds.length === 0 || !hasCollectionProcessor
                    }
                    onClick={handleGenerateLink}
                  >
                    {intl.formatMessage({ id: "gestion.financials.generateLink" })}
                  </Button>
                )}
              </Stack>
              {routes?.paymentLinks && !hasCollectionProcessor && (
                <Typography variant="caption" color="text.secondary">
                  {intl.formatMessage({ id: "gestion.financials.linkNeedsProcessor" })}
                </Typography>
              )}
            </Stack>
          )}

          {awaitingGroups.length > 0 && (
            <Stack spacing={2}>
              <Typography variant="subtitle1">
                {intl.formatMessage({ id: "gestion.financials.awaitingPayment" })}
              </Typography>
              {awaitingGroups.map((group) => {
                const total = group.installments.reduce(
                  (sum, installment) => sum + Number(installment.amount || 0),
                  0,
                );

                return (
                  <Stack
                    key={group.paymentId}
                    spacing={1}
                    sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1.5 }}
                  >
                    {group.installments.map((installment) => (
                      <Typography key={installment.id} variant="body2">
                        {installmentLabel(installment, intl)} — {formatAmount(installment.amount, installment.currency_code || currencyCode)}
                      </Typography>
                    ))}
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {intl.formatMessage({ id: "gestion.financials.amount" })}: {formatAmount(total, currencyCode)}
                    </Typography>
                    {group.paymentUrl && (
                      <Link href={group.paymentUrl} target="_blank" rel="noopener noreferrer" sx={{ wordBreak: "break-all" }}>
                        {group.paymentUrl}
                      </Link>
                    )}
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!group.paymentUrl}
                        onClick={() => handleCopyLink(group.paymentUrl)}
                      >
                        {intl.formatMessage({ id: "gestion.financials.copyLink" })}
                      </Button>
                      {group.expiresAt && (
                        <Typography variant="caption" color="text.secondary">
                          {intl.formatMessage(
                            { id: "gestion.financials.linkExpiresAt" },
                            { time: dayjs(group.expiresAt).format("DD/MM/YYYY HH:mm") },
                          )}
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                );
              })}
            </Stack>
          )}
        </Stack>
      )}

      <Dialog open={voidConfirmOpen} onClose={() => !voiding && setVoidConfirmOpen(false)}>
        <DialogTitle>{intl.formatMessage({ id: "membership.panel.voidConfirmTitle" })}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {intl.formatMessage({ id: "membership.panel.voidConfirmBody" }, { count: selectedIds.length })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoidConfirmOpen(false)} disabled={voiding}>
            {intl.formatMessage({ id: "membership.panel.voidConfirmCancel" })}
          </Button>
          <Button color="warning" variant="contained" onClick={() => voidSelectedRef.current()} disabled={voiding}>
            {voiding
              ? intl.formatMessage({ id: "membership.panel.voidingButton" })
              : intl.formatMessage({ id: "membership.panel.voidConfirm" })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MembershipBillingPanel;
